import { cn } from '@utazzo/ui';
import React from 'react';
import { Link } from 'react-router-dom';

import { type KpiTone } from './KpiCard';

import type { LucideIcon } from 'lucide-react';

// ---------------------------------------------------------------------------
// Lead Inbox redesign (Claude Design v1) - presentational pool / performance
// cards. See docs/reporting/lead-inbox-redesign-spec.md. The board is a fixed
// teal-on-#fafafa design, so colours are intentionally hard-coded (they vary
// per card, which Tailwind's static JIT cannot resolve from a variable); layout
// and hover use utility classes. Every card is pure chrome around the drill
// handlers the page already owns.
// ---------------------------------------------------------------------------

export const INK = '#094053';

/** en-IN thousands formatting for the card numbers. */
export const ninIN = (value: number): string => value.toLocaleString('en-IN');

/** Share-of-whole, one decimal, divide-by-zero guarded (never NaN/Infinity). */
export const sharePct = (part: number, whole: number): string =>
  whole > 0 ? `${((part / whole) * 100).toFixed(1)}%` : '0%';

/** Fraction 0-100 for a bar width, guarded. */
export const barPct = (part: number, whole: number): number =>
  whole > 0 ? Math.max(0, Math.min(100, (part / whole) * 100)) : 0;

export const POOL_CARD =
  'flex flex-col gap-[9px] rounded-xl border border-[#e4e4e7] bg-white px-[14px] py-[12px] shadow-[0_1px_2px_rgba(0,0,0,0.04)] transition-[border-color,box-shadow]';
const POOL_CARD_HOVER =
  'cursor-pointer hover:border-[#a1a1aa] hover:shadow-[0_4px_12px_rgba(0,0,0,0.06)]';
const PERF_CARD =
  'flex flex-col gap-[9px] rounded-xl border border-[#e4e4e7] bg-white px-[14px] py-[12px] shadow-[0_1px_2px_rgba(0,0,0,0.03)] transition-[border-color,box-shadow] hover:border-[#a1a1aa] hover:shadow-[0_10px_24px_-16px_rgba(0,0,0,0.3)]';

export const SharePill = ({
  value,
  title,
}: {
  value: string;
  title: string;
}): React.JSX.Element => (
  <span
    title={title}
    className="rounded-full bg-[#e3edf0] px-[7px] py-[2px] text-[11px] font-bold whitespace-nowrap text-[#094053]"
  >
    {value}
  </span>
);

/** Two-segment stacked bar: solid "this month" + faded "carry forward". */
export const StackBar = ({
  color,
  pct,
  height = 5,
  fade = 0.25,
}: {
  color: string;
  pct: number;
  height?: number;
  fade?: number;
}): React.JSX.Element => (
  <div className="flex gap-[2px]" style={{ height }}>
    <span className="rounded-[3px]" style={{ width: `${pct}%`, background: color }} />
    <span className="flex-1 rounded-[3px]" style={{ background: color, opacity: fade }} />
  </div>
);

/** A bold tabular foot number; a button (with its own drill) when onClick is set. */
export const FootNum = ({
  value,
  onClick,
}: {
  value: number;
  onClick?: () => void;
}): React.JSX.Element =>
  onClick ? (
    <button
      type="button"
      onClick={(event) => {
        event.stopPropagation();
        onClick();
      }}
      className="font-semibold text-[#094053] tabular-nums underline-offset-2 hover:underline"
    >
      {ninIN(value)}
    </button>
  ) : (
    <b className="font-semibold text-[#094053] tabular-nums">{ninIN(value)}</b>
  );

/** "This month N / Carry N" foot row shared by every pool card. */
export const FootSplit = ({
  month,
  carry,
  onMonth,
  onCarry,
}: {
  month: number;
  carry: number;
  onMonth?: () => void;
  onCarry?: () => void;
}): React.JSX.Element => (
  <div className="flex justify-between gap-2 text-[11px] whitespace-nowrap text-[#71717a]">
    <span>
      This month <FootNum value={month} onClick={onMonth} />
    </span>
    <span>
      Carry <FootNum value={carry} onClick={onCarry} />
    </span>
  </div>
);

export const HeroStat = ({
  label,
  value,
  color,
  onClick,
}: {
  label: string;
  value: number;
  color: string;
  onClick?: () => void;
}): React.JSX.Element => (
  <div className="flex flex-col gap-[2px]">
    <span className="text-[10px] font-semibold tracking-[0.04em] text-[#71717a] uppercase">
      {label}
    </span>
    {onClick ? (
      <button
        type="button"
        onClick={(event) => {
          event.stopPropagation();
          onClick();
        }}
        className="text-left text-[18px] leading-none font-semibold tabular-nums underline-offset-2 hover:underline"
        style={{ color }}
      >
        {ninIN(value)}
      </button>
    ) : (
      <span className="text-[18px] leading-none font-semibold tabular-nums" style={{ color }}>
        {ninIN(value)}
      </span>
    )}
  </div>
);

/** Total-pool hero card (spans two columns). */
export const PoolHeroCard = ({
  total,
  month,
  carry,
  unassigned,
  onClick,
  onMonth,
  onCarry,
}: {
  total: number;
  month: number;
  carry: number;
  unassigned: number;
  onClick?: () => void;
  onMonth?: () => void;
  onCarry?: () => void;
}): React.JSX.Element => (
  <div
    onClick={onClick}
    className={cn('col-span-2 justify-between gap-3', POOL_CARD, POOL_CARD_HOVER)}
  >
    <div className="flex items-center justify-between gap-2">
      <div className="text-[12px] font-semibold text-[#52525b]">Total pool</div>
      <span className="rounded-full bg-[#eff6ff] px-[8px] py-[2px] text-[10px] font-semibold whitespace-nowrap text-[#1d4ed8]">
        incl. {ninIN(unassigned)} unassigned
      </span>
    </div>
    <div className="flex flex-wrap items-end justify-between gap-3">
      <div className="text-[40px] leading-none font-semibold tracking-[-0.03em] text-[#094053] tabular-nums">
        {ninIN(total)}
      </div>
      <div className="flex gap-[18px]">
        <HeroStat label="This month" value={month} color="#094053" onClick={onMonth} />
        <HeroStat label="Carry fwd" value={carry} color="#52525b" onClick={onCarry} />
      </div>
    </div>
    <StackBar color="#094053" pct={barPct(month, total)} height={6} fade={0.22} />
  </div>
);

/** A status / queue pool card: dot, big number + share pill, stacked bar, foot. */
export const PoolMetricCard = ({
  label,
  dot,
  value,
  numberColor,
  barColor,
  share,
  shareTitle,
  month,
  carry,
  onClick,
  onMonth,
  onCarry,
}: {
  label: string;
  dot: string;
  value: number;
  numberColor: string;
  barColor: string;
  share: string;
  shareTitle: string;
  month: number;
  carry: number;
  onClick?: () => void;
  onMonth?: () => void;
  onCarry?: () => void;
}): React.JSX.Element => (
  <div onClick={onClick} className={cn(POOL_CARD, POOL_CARD_HOVER)}>
    <div className="flex items-center gap-[6px] text-[12px] font-semibold text-[#52525b]">
      <span className="h-2 w-2 rounded-full" style={{ background: dot }} />
      {label}
    </div>
    <div className="flex flex-wrap items-baseline gap-2">
      <div
        className="text-[26px] leading-none font-semibold tracking-[-0.02em] tabular-nums"
        style={{ color: numberColor }}
      >
        {ninIN(value)}
      </div>
      <SharePill value={share} title={shareTitle} />
    </div>
    <StackBar color={barColor} pct={barPct(month, value)} />
    <FootSplit month={month} carry={carry} onMonth={onMonth} onCarry={onCarry} />
  </div>
);

/** One Proposal/Warm/Hot sub-block inside the Active breakup card. */
export const ActiveSubBlock = ({
  label,
  dotOpacity,
  value,
  share,
  month,
  carry,
  onClick,
  onMonth,
  onCarry,
}: {
  label: string;
  dotOpacity: number;
  value: number;
  share: string;
  month: number;
  carry: number;
  onClick?: () => void;
  onMonth?: () => void;
  onCarry?: () => void;
}): React.JSX.Element => (
  <div
    onClick={onClick}
    className="-mx-2 flex cursor-pointer flex-col gap-2 rounded-lg px-2 py-[6px] hover:bg-[#f4f4f5]"
  >
    <div className="flex items-center gap-[6px] text-[12px] font-semibold text-[#52525b]">
      <span className="h-2 w-2 rounded-full bg-[#1d4ed8]" style={{ opacity: dotOpacity }} />
      {label}
    </div>
    <div className="flex flex-wrap items-baseline gap-2">
      <div className="text-[24px] leading-none font-semibold tracking-[-0.02em] text-[#094053] tabular-nums">
        {ninIN(value)}
      </div>
      <SharePill value={share} title="share of Active" />
    </div>
    <StackBar color="#1d4ed8" pct={barPct(month, value)} />
    <FootSplit month={month} carry={carry} onMonth={onMonth} onCarry={onCarry} />
  </div>
);

/** One stage column inside the Active funnel (sitting now vs ever entered). */
export const FunnelStageCard = ({
  label,
  dotOpacity,
  total,
  monthCur,
  monthMtd,
  carryCur,
  carryCum,
}: {
  label: string;
  dotOpacity: number;
  total: number;
  monthCur: number;
  monthMtd: number;
  carryCur: number;
  carryCum: number;
}): React.JSX.Element => (
  <div className="flex flex-col gap-[10px] rounded-xl border border-[#f4f4f5] bg-[#fafafa] p-[12px]">
    <div className="flex items-center justify-between gap-2 whitespace-nowrap">
      <div className="flex items-center gap-[7px] text-[12px] font-semibold text-[#52525b]">
        <span
          className="h-[9px] w-[9px] rounded-full bg-[#094053]"
          style={{ opacity: dotOpacity }}
        />
        {label}
      </div>
      <span className="text-[11px] text-[#71717a]">
        total <b className="text-[13px] font-bold text-[#094053]">{ninIN(total)}</b>
      </span>
    </div>
    <div className="flex flex-col gap-[5px]">
      <div className="flex items-baseline justify-between gap-2 whitespace-nowrap">
        <span className="text-[10.5px] font-semibold text-[#71717a]">This month</span>
        <span className="text-[12px] text-[#71717a]">
          <b className="text-[15px] font-bold text-[#094053]">{ninIN(monthCur)}</b>{' '}
          <span className="opacity-70">/ {ninIN(monthMtd)} MTD</span>
        </span>
      </div>
      <div className="h-[5px] overflow-hidden rounded-[3px] bg-[#dbe7ec]">
        <div
          className="h-full rounded-[3px] bg-[#094053]"
          style={{ width: `${barPct(monthCur, monthMtd)}%` }}
        />
      </div>
    </div>
    <div className="flex flex-col gap-[5px]">
      <div className="flex items-baseline justify-between gap-2 whitespace-nowrap">
        <span className="text-[10.5px] font-semibold text-[#71717a]">Carry forward</span>
        <span className="text-[12px] text-[#71717a]">
          <b className="text-[15px] font-bold text-[#094053]">{ninIN(carryCur)}</b>{' '}
          <span className="opacity-70">/ {ninIN(carryCum)} cum.</span>
        </span>
      </div>
      <div className="h-[5px] overflow-hidden rounded-[3px] bg-[#e6ecef]">
        <div
          className="h-full rounded-[3px] bg-[#6b8f9c]"
          style={{ width: `${barPct(carryCur, carryCum)}%` }}
        />
      </div>
    </div>
  </div>
);

/** A single-value Performance KPI card. */
export const PerfCard = ({
  label,
  dot,
  value,
  valueColor,
  hint,
  className,
  onClick,
}: {
  label: string;
  dot: string;
  value: React.ReactNode;
  valueColor: string;
  hint: React.ReactNode;
  className?: string;
  /** When set, the whole card drills to a filtered lead view. */
  onClick?: () => void;
}): React.JSX.Element => (
  <div
    className={cn(PERF_CARD, onClick && 'cursor-pointer', className)}
    {...(onClick
      ? {
          role: 'button',
          tabIndex: 0,
          onClick,
          onKeyDown: (e: React.KeyboardEvent) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onClick();
            }
          },
        }
      : {})}
  >
    <div className="flex items-center gap-[7px] text-[12px] font-semibold whitespace-nowrap text-[#52525b]">
      <span className="h-2 w-2 rounded-full" style={{ background: dot }} />
      {label}
    </div>
    <div
      className="text-[26px] leading-none font-semibold tracking-[-0.03em] whitespace-nowrap"
      style={{ color: valueColor }}
    >
      {value}
    </div>
    <div className="truncate text-[11px] whitespace-nowrap text-[#71717a]">{hint}</div>
  </div>
);

/** A Performance KPI card with N sub-stats under a hairline (optionally red-tinted). */
export const PerfSplitCard = ({
  label,
  dot,
  parts,
  tint = false,
  className,
}: {
  label: string;
  dot: string;
  parts: Array<{
    value: React.ReactNode;
    valueColor?: string;
    label: string;
    hint?: React.ReactNode;
  }>;
  tint?: boolean;
  className?: string;
}): React.JSX.Element => (
  <div className={cn(PERF_CARD, tint && 'border-[#fecaca] bg-[#fff5f5]', className)}>
    <div className="flex items-center gap-[7px] text-[12px] font-semibold whitespace-nowrap text-[#52525b]">
      <span className="h-2 w-2 rounded-full" style={{ background: dot }} />
      {label}
    </div>
    <div
      className={cn(
        'grid gap-[10px] border-t pt-[4px]',
        tint ? 'border-[#fecaca]' : 'border-[#f4f4f5]',
        parts.length >= 4 ? 'grid-cols-4' : parts.length === 3 ? 'grid-cols-3' : 'grid-cols-2',
      )}
    >
      {parts.map((part, index) => (
        <div key={index} className="flex min-w-0 flex-col gap-[4px]">
          <div
            className="text-[22px] leading-none font-semibold tracking-[-0.03em] whitespace-nowrap"
            style={{ color: part.valueColor ?? INK }}
          >
            {part.value}
          </div>
          <div className="text-[11px] font-semibold whitespace-nowrap text-[#52525b]">
            {part.label}
          </div>
          {part.hint !== undefined && part.hint !== null && (
            <div className="text-[10.5px] whitespace-nowrap text-[#71717a]">{part.hint}</div>
          )}
        </div>
      ))}
    </div>
  </div>
);

/** Uppercase section mini-label with a faint period suffix, matching the mockup. */
export const SectionLabel = ({
  title,
  suffix,
}: {
  title: string;
  suffix?: string;
}): React.JSX.Element => (
  <div className="text-[11px] font-semibold tracking-[0.06em] whitespace-nowrap text-[#71717a] uppercase">
    {title}
    {suffix ? <span className="font-medium text-[#a1a1aa]"> · {suffix}</span> : null}
  </div>
);

// ---------------------------------------------------------------------------
// Report-tab KPI cards (Claude Design "Lead Inbox" mockup). Restyled 2026-10-02
// from the shared KpiCard/KpiPairCard to the same status-dot + big tone-coloured
// number + hint look as the Daily Tracker / Lead Pool / Performance bands. Kept
// LOCAL (not the shared component) so the restyle is scoped to the lead report
// tabs - the shared KpiCard still serves dashboard / marketing / callsense /
// ticketing. Prop shape mirrors the old cards so the tab call sites are
// unchanged; `icon` / `compact` are accepted for compatibility but no longer
// used (the colour-coded dot replaces the icon).
// ---------------------------------------------------------------------------
const KPI_DOT: Record<KpiTone, string> = {
  default: '#a1a1aa',
  primary: '#3b82f6',
  success: '#10b981',
  warning: '#f59e0b',
  danger: '#ef4444',
};
const KPI_VALUE_COLOR: Record<KpiTone, string> = {
  default: INK,
  primary: '#1d4ed8',
  success: INK,
  warning: '#b45309',
  danger: '#b91c1c',
};
const REPORT_KPI_CHROME =
  'flex flex-col gap-[9px] rounded-xl border border-[#e4e4e7] bg-white px-[14px] py-[12px] shadow-[0_1px_2px_rgba(0,0,0,0.03)]';
const REPORT_KPI_HOVER =
  'cursor-pointer transition-[border-color,box-shadow] hover:border-[#a1a1aa] hover:shadow-[0_10px_24px_-16px_rgba(0,0,0,0.3)]';

interface ReportKpiCardProps {
  label: string;
  value: React.ReactNode;
  /** Accepted for call-site compatibility; the dot replaces it (not rendered). */
  icon?: LucideIcon;
  tone?: KpiTone;
  hint?: React.ReactNode;
  to?: string;
  onClick?: () => void;
  /** Accepted for compatibility; the mockup card is a single size. */
  compact?: boolean;
  className?: string;
}

export const ReportKpiCard = ({
  label,
  value,
  tone = 'default',
  hint,
  to,
  onClick,
  className,
}: ReportKpiCardProps): React.JSX.Element => {
  const inner = (
    <>
      <div className="flex items-center gap-[7px] text-[12px] font-semibold whitespace-nowrap text-[#52525b]">
        <span className="h-2 w-2 shrink-0 rounded-full" style={{ background: KPI_DOT[tone] }} />
        <span className="truncate">{label}</span>
      </div>
      <div
        className="line-clamp-2 text-[26px] leading-tight font-semibold tracking-[-0.03em] break-words"
        style={{ color: KPI_VALUE_COLOR[tone] }}
      >
        {value}
      </div>
      {hint !== undefined && hint !== null && (
        <div className="truncate text-[11px] whitespace-nowrap text-[#71717a]">{hint}</div>
      )}
    </>
  );
  if (to) {
    return (
      <Link to={to} className={cn(REPORT_KPI_CHROME, REPORT_KPI_HOVER, className)}>
        {inner}
      </Link>
    );
  }
  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        className={cn(REPORT_KPI_CHROME, REPORT_KPI_HOVER, 'text-left', className)}
      >
        {inner}
      </button>
    );
  }
  return <div className={cn(REPORT_KPI_CHROME, className)}>{inner}</div>;
};

interface ReportKpiPairCardProps {
  label: string;
  icon?: LucideIcon;
  tone?: KpiTone;
  parts: Array<{ label: string; value: React.ReactNode; hint?: React.ReactNode }>;
  className?: string;
  onClick?: () => void;
  compact?: boolean;
}

export const ReportKpiPairCard = ({
  label,
  tone = 'default',
  parts,
  className,
  onClick,
}: ReportKpiPairCardProps): React.JSX.Element => {
  // `danger` tone reads as the red-tinted period-of-loss card (matches the
  // Performance band's PerfSplitCard tint).
  const tint = tone === 'danger';
  const body = (
    <>
      <div className="flex items-center gap-[7px] text-[12px] font-semibold whitespace-nowrap text-[#52525b]">
        <span className="h-2 w-2 shrink-0 rounded-full" style={{ background: KPI_DOT[tone] }} />
        <span className="truncate">{label}</span>
      </div>
      <div
        className={cn(
          'grid gap-[10px] border-t pt-[4px]',
          tint ? 'border-[#fecaca]' : 'border-[#f4f4f5]',
          parts.length >= 4 ? 'grid-cols-4' : parts.length === 3 ? 'grid-cols-3' : 'grid-cols-2',
        )}
      >
        {parts.map((part, index) => (
          <div key={index} className="flex min-w-0 flex-col gap-[4px]">
            <div
              className="text-[22px] leading-none font-semibold tracking-[-0.03em] whitespace-nowrap"
              style={{ color: KPI_VALUE_COLOR[tone] }}
            >
              {part.value}
            </div>
            <div className="text-[11px] font-semibold whitespace-nowrap text-[#52525b]">
              {part.label}
            </div>
            {part.hint !== undefined && part.hint !== null && (
              <div className="text-[10.5px] whitespace-nowrap text-[#71717a]">{part.hint}</div>
            )}
          </div>
        ))}
      </div>
    </>
  );
  const chrome = cn(
    REPORT_KPI_CHROME,
    'text-left',
    tint && 'border-[#fecaca] bg-[#fff5f5]',
    onClick && REPORT_KPI_HOVER,
    className,
  );
  if (onClick) {
    return (
      <button type="button" onClick={onClick} className={chrome}>
        {body}
      </button>
    );
  }
  return <div className={chrome}>{body}</div>;
};
